import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';

export const PlaytreeDataContext = createContext({
  playtrees: [],
  playtreesLoading: false,
  playtreesError: null,
  fetchPlaytrees: async () => {},
  createPlaytree: async () => {},
  updatePlaytreeName: async () => {},
  removePlaylistFromPlaytree: async () => {},
  addPlaylistToPlaytree: async () => {},
  deletePlaytree: async () => {},
  updateSublistOrder: async () => {},
});

export const normalizeSublists = (sublists) => {
  if (!sublists || sublists.length === 0) return [];
  if (typeof sublists[0] === 'string') return sublists;
  return [...sublists]
    .sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity))
    .map(entry => entry.playlistId);
};

const PlaytreeDataProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [playtrees, setPlaytrees] = useState([]);
  const [playtreesLoading, setPlaytreesLoading] = useState(false);
  const [playtreesError, setPlaytreesError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => {
    addNotification(message, 'success');
  };

  const showErrorNotification = (message, requestBody) => {
    addNotification(`${message}. Request: ${JSON.stringify(requestBody)}`, 'error');
  };

  const removeNotification = (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  // Fetch playtrees
  const fetchPlaytrees = async () => {
    if (authData?.accessToken) {
      setPlaytreesLoading(true);
      setPlaytreesError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/playtrees/`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch playtrees');
        }

        const data = await response.json();
        setPlaytrees(data.map(pt => ({
          ...pt,
          sublists: normalizeSublists(pt.sublists),
        })));
      } catch (error) {
        setPlaytreesError(error.message);
      } finally {
        setPlaytreesLoading(false);
      }
    }
  };

  // Create a new playtree
  const createPlaytree = async (playtreeData) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/playtrees/`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(playtreeData),
        });

        if (!response.ok) {
          throw new Error('Failed to create playtree');
        }

        const newPlaytree = await response.json();
        setPlaytrees((prev) => [...prev, {
          ...newPlaytree,
          sublists: normalizeSublists(newPlaytree.sublists),
        }]);
        showSuccessNotification(`Playtree created: ${newPlaytree.name}`);
      } catch (error) {
        showErrorNotification(error.message, JSON.stringify(playtreeData));
      }
    }
  };

  // Update playtree name
  const updatePlaytreeName = async (playtreeId, newName) => {
    if (authData?.accessToken) {
      const requestBody = { name: newName }; // Only send the name key

      try {
        const response = await fetch(
          `${API_BASE_URL}/playtrees/${playtreeId}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update playtree name');
        }

        // Update the playtree in the local cache
        setPlaytrees((prevPlaytrees) =>
          prevPlaytrees.map((playtree) =>
            playtree.id === playtreeId ? { ...playtree, name: newName } : playtree
          )
        );

        showSuccessNotification(`Playtree name updated to: ${newName}`);
      } catch (error) {
        showErrorNotification(error.message, requestBody);
        throw error;
      }
    }
  };

  // Remove a playlist from a playtree
  const removePlaylistFromPlaytree = async (playtreeId, playlistId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/playtrees/${playtreeId}/sublist/${playlistId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to remove playlist from playtree');
        }

        // Update the playtree's sublists locally
        setPlaytrees((prevPlaytrees) =>
          prevPlaytrees.map((playtree) =>
            playtree.id === playtreeId
              ? {
                  ...playtree,
                  sublists: playtree.sublists.filter((id) => id !== playlistId),
                }
              : playtree
          )
        );

        showSuccessNotification(`Playlist removed from playtree.`);
      } catch (error) {
        showErrorNotification(error.message, { playtreeId, playlistId });
        throw error;
      }
    }
  };

  // Add a playlist to a playtree
  const addPlaylistToPlaytree = async (playtreeId, playlistId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/playtrees/${playtreeId}/sublist/${playlistId}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to add playlist to playtree');
        }

        // Update the playtree's sublists locally
        setPlaytrees((prevPlaytrees) =>
          prevPlaytrees.map((playtree) =>
            playtree.id === playtreeId
              ? {
                  ...playtree,
                  sublists: [...playtree.sublists, playlistId],
                }
              : playtree
          )
        );

        showSuccessNotification(`Playlist added to playtree.`);
      } catch (error) {
        showErrorNotification(error.message, { playtreeId, playlistId });
      }
    }
  };

  const updateSublistOrder = async (playtreeId, orderedPlaylistIds) => {
    if (!authData?.accessToken) return;

    const previousPlaytrees = playtrees;

    setPlaytrees(prev =>
      prev.map(pt =>
        pt.id === playtreeId ? { ...pt, sublists: [...orderedPlaylistIds] } : pt
      )
    );

    try {
      const response = await fetch(
        `${API_BASE_URL}/playtrees/${playtreeId}/sublist-order`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(orderedPlaylistIds),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update playlist order');
      }

      showSuccessNotification('Playlist order updated.');
    } catch (error) {
      setPlaytrees(previousPlaytrees);
      showErrorNotification(error.message, { playtreeId, orderedPlaylistIds });
      throw error;
    }
  };

  const deletePlaytree = async (playtreeId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/playtrees/${playtreeId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to delete playtree');
        }
  
        // Remove the deleted playtree from the local state
        setPlaytrees((prevPlaytrees) =>
          prevPlaytrees.filter((playtree) => playtree.id !== playtreeId)
        );
  
        showSuccessNotification('Playtree deleted successfully.');
      } catch (error) {
        showErrorNotification(error.message, { playtreeId });
      }
    }
  };
  

  useEffect(() => {
    fetchPlaytrees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <PlaytreeDataContext.Provider
      value={{
        playtrees,
        playtreesLoading,
        playtreesError,
        fetchPlaytrees,
        createPlaytree,
        updatePlaytreeName,
        removePlaylistFromPlaytree,
        addPlaylistToPlaytree,
        deletePlaytree,
        updateSublistOrder
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </PlaytreeDataContext.Provider>
  );
};

export default PlaytreeDataProvider;
